import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import type { Product } from '../auth/api';

/**
 * 一件商品。
 *
 * 两个金额**都要显示，而且要分得开**：
 *
 *   price   付多少钱   —— 按钮上那个数
 *   coins   到账多少   —— 含赠送，特卖时比 price 大
 *
 * 只显示一个的话特卖就没法表达了（"付 50 得 60"是这类商品的全部卖点），
 * 而只显示到账额会让人以为付的是那个数。
 */
export function ProductCard(
  { p, money, busy, onBuy }:
  { p: Product; money(n: number): string; busy: boolean; onBuy(): void },
) {
  const { t } = useI18n();
  const bonus = p.coins - p.price;

  return (
    <div className={`shop__card${p.canBuy ? '' : ' shop__card--off'}`}>
      {/* 角标：后端只给一个稳定的 key，**文案在前端翻** —— 后端不知道
          这个用户选了哪种语言。认不出来的 key 就不显示：
          不显示总比显示一个原始 key 好。 */}
      {p.tag && TAGS.has(p.tag) && (
        <span className={`shop__tag shop__tag--${p.tag}`}>
          {t(`shop.tag.${p.tag}` as 'shop.tag.hot')}
        </span>
      )}

      <div className="shop__coins num">{money(p.coins)}</div>

      {/*
        多送 和 划线价 **只画一个** —— 两个都画的时候它们在说同一件事：
        付 50 得 65，既是"多送 15"也是"原价 65 现价 50"。并排放着
        读起来像两个优惠叠加了，而实际只有一个。

        实测踩过更难看的一种：coins 和 original_price 是同一个数字，
        卡上就出现 `R$60.00` 和 `原价 R$60.00` 并排 —— 像个 bug。

        **金币类优先用"多送"**：买的就是钱本身，"原价"在这里不是个自然的说法。
        划线价留给以后 kind 不是 coins 的商品 —— 道具、月卡那些有标价的东西。
      */}
      {bonus > 0 ? (
        <span className="shop__bonus">{t('shop.bonus', { v: money(bonus) })}</span>
      ) : p.originalPrice !== null ? (
        <span className="shop__orig">
          {t('shop.origPrice', { v: money(p.originalPrice) })}
        </span>
      ) : null}

      {/* 限购状态：**买满了要说"今天买满了"**，不是只给一个灰按钮 ——
          灰按钮不告诉人明天还能买。 */}
      {p.dailyLimit !== null && (
        <span className="shop__limit small dim">
          {p.left === 0
            ? t('shop.soldOut')
            : t('shop.leftToday', { n: p.left ?? 0 })}
        </span>
      )}

      <Button variant={p.canBuy ? 'primary' : 'ghost'} className="btn--wide"
              disabled={!p.canBuy || busy} onClick={onBuy}>
        {busy ? t('action.working') : money(p.price)}
      </Button>
    </div>
  );
}

/** 认得出来的角标。**不在这里的就不显示** —— 显示一个原始 key 更糟。 */
const TAGS = new Set(['hot', 'best', 'daily', 'first']);
